'use client';

import { GoogleMap, Marker } from '@react-google-maps/api';
import { Card, CardContent, useGoogleMapsLoader } from '@up2stay/up2promo-ui';
import { MapPin, Minus, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { usePreferencesStore } from '@/core/store/preferences.store';
import { getSystemTheme } from '@/core/utils/theme';

import { env } from '@/core/env';

/** Map style entry for Google Styled Maps */
type MapStyleEntry = {
  elementType?: string;
  featureType?: string;
  stylers: Array<{ color?: string; visibility?: string }>;
};

/** Matches DARK_MAP_STYLES geometry — set on the container before tiles load */
const MAP_SURFACE_DARK = '#1e293b';
const MAP_SURFACE_LIGHT = '#e2e8f0';

/** Hide all POIs and transit so only our marker is visible */
const HIDE_POIS_STYLES: MapStyleEntry[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

/** Dark/night mode map styles (Google Styled Maps) – matches app dark theme */
const DARK_MAP_STYLES: MapStyleEntry[] = [
  { elementType: 'geometry', stylers: [{ color: MAP_SURFACE_DARK }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: MAP_SURFACE_DARK }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#cbd5e1' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#cbd5e1' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#334155' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#64748b' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#334155' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: MAP_SURFACE_DARK }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#94a3b8' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#475569' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#334155' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#e2e8f0' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#334155' }],
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#cbd5e1' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0f172a' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#475569' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#0f172a' }],
  },
];

const MAP_CONTAINER_CLASS = 'h-full w-full overflow-hidden';
const MAP_CONTAINER_CLASS_TALL = `${MAP_CONTAINER_CLASS} min-h-[380px] rounded-b-lg`;

const MIN_ZOOM = 10;
const MAX_ZOOM = 20;

function mapSurfaceColor(isDark: boolean): string {
  return isDark ? MAP_SURFACE_DARK : MAP_SURFACE_LIGHT;
}

/** Custom zoom control overlay – theme-aware, minimal */
function MapZoomControl({ map }: { map: google.maps.Map | null }) {
  const zoomIn = useCallback(() => {
    if (!map) return;
    const z = map.getZoom() ?? 16;
    map.setZoom(Math.min(MAX_ZOOM, z + 1));
  }, [map]);
  const zoomOut = useCallback(() => {
    if (!map) return;
    const z = map.getZoom() ?? 16;
    map.setZoom(Math.max(MIN_ZOOM, z - 1));
  }, [map]);
  if (!map) return null;
  return (
    <div className="absolute right-3 top-3 flex flex-col gap-px overflow-hidden rounded-lg border border-border bg-card/95 shadow-sm backdrop-blur-sm">
      <button
        type="button"
        onClick={zoomIn}
        className="flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-transparent"
        aria-label="Zoom in"
      >
        <Plus className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={zoomOut}
        className="flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-transparent"
        aria-label="Zoom out"
      >
        <Minus className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Themed empty/loading surface — never a second map provider */
function MapSurfacePlaceholder({
  isDark,
  label,
  tall,
}: {
  isDark: boolean;
  label?: string;
  tall?: boolean;
}) {
  return (
    <div
      className={`${tall ? MAP_CONTAINER_CLASS_TALL : MAP_CONTAINER_CLASS} flex flex-col items-center justify-center gap-2`}
      style={{ backgroundColor: mapSurfaceColor(isDark) }}
      aria-busy={!label}
    >
      {label ? (
        <>
          <MapPin className="h-10 w-10 text-muted-foreground" aria-hidden />
          <p className="px-4 text-center text-sm text-muted-foreground">
            {label}
          </p>
        </>
      ) : null}
    </div>
  );
}

interface LocationMapPreviewProps {
  latitude: number;
  longitude: number;
  name?: string;
  mapPreviewLabel?: string;
  noCoordinatesLabel?: string;
  /** Shown when Google Maps API key is not configured (dev / misconfig). */
  mapUnavailableLabel?: string;
  /** Hint under an interactive map (e.g. drag pin). */
  dragHintLabel?: string;
  /** When true, uses a smaller height for use in grids (e.g. detail overview). */
  compact?: boolean;
  /**
   * Render map surface only (no Card chrome / title row).
   * Use when a parent already provides the label / collapsible shell.
   */
  bare?: boolean;
  /**
   * Allow panning the map and dragging the pin.
   * Pair with `onPositionChange` to persist new coordinates.
   */
  interactive?: boolean;
  onPositionChange?: (latitude: number, longitude: number) => void;
}

/** Renders map via Google Maps JS API; only mounted when apiKey is non-empty. */
function LocationMapWithGoogleApi({
  apiKey,
  latitude,
  longitude,
  name,
  mapPreviewLabel,
  isDark,
  tall,
  interactive,
  onPositionChange,
}: {
  apiKey: string;
  latitude: number;
  longitude: number;
  name?: string;
  mapPreviewLabel: string;
  isDark: boolean;
  tall: boolean;
  interactive: boolean;
  onPositionChange?: (latitude: number, longitude: number) => void;
}) {
  const { isLoaded } = useGoogleMapsLoader(apiKey);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);
  const onMapUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const surface = mapSurfaceColor(isDark);
  const center = useMemo(
    () => ({ lat: latitude, lng: longitude }),
    [latitude, longitude],
  );
  const mapOptions = useMemo(
    () => ({
      zoom: 16,
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      scaleControl: false,
      rotateControl: false,
      backgroundColor: surface,
      styles: [...HIDE_POIS_STYLES, ...(isDark ? DARK_MAP_STYLES : [])],
      draggable: interactive,
      scrollwheel: interactive,
      disableDoubleClickZoom: !interactive,
      // Inside scrolling forms: wheel / one finger scroll the page; zoom
      // needs Ctrl/Cmd + wheel or two fingers (DEV-548).
      gestureHandling: interactive
        ? ('cooperative' as const)
        : ('none' as const),
      keyboardShortcuts: interactive,
    }),
    [interactive, isDark, surface],
  );
  const mapContainerStyle = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      backgroundColor: surface,
    }),
    [surface],
  );

  const handleMarkerDragEnd = useCallback(
    (event: google.maps.MapMouseEvent) => {
      const lat = event.latLng?.lat();
      const lng = event.latLng?.lng();
      if (lat == null || lng == null) return;
      onPositionChange?.(lat, lng);
    },
    [onPositionChange],
  );

  if (!isLoaded) {
    return <MapSurfacePlaceholder isDark={isDark} tall={tall} />;
  }

  return (
    <>
      <GoogleMap
        mapContainerClassName={
          tall ? MAP_CONTAINER_CLASS_TALL : MAP_CONTAINER_CLASS
        }
        mapContainerStyle={mapContainerStyle}
        center={center}
        options={mapOptions}
        zoom={16}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
      >
        <Marker
          position={center}
          title={name || mapPreviewLabel}
          draggable={interactive}
          onDragEnd={interactive ? handleMarkerDragEnd : undefined}
        />
      </GoogleMap>
      <MapZoomControl map={map} />
    </>
  );
}

export function LocationMapPreview({
  latitude,
  longitude,
  name,
  mapPreviewLabel = 'Map preview',
  noCoordinatesLabel = 'No coordinates',
  mapUnavailableLabel = 'Map unavailable',
  dragHintLabel,
  compact = false,
  bare = false,
  interactive = false,
  onPositionChange,
}: LocationMapPreviewProps) {
  const themeSetting = usePreferencesStore((s) => s.themeSetting);
  const isDark = useMemo(() => {
    const resolved =
      themeSetting === 'system' ? getSystemTheme() : themeSetting;
    return resolved === 'dark';
  }, [themeSetting]);

  const apiKey = env.VITE_GOOGLE_MAPS_API_KEY ?? '';
  const hasApiKey = apiKey.length > 0;
  const surface = mapSurfaceColor(isDark);

  const isValid =
    !Number.isNaN(latitude) &&
    !Number.isNaN(longitude) &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude);

  const mapSurface = (
    <div className="flex flex-col gap-2">
      <div
        className={
          compact
            ? 'relative aspect-[16/10] max-h-[220px] w-full overflow-hidden'
            : 'relative aspect-[4/3] w-full overflow-hidden sm:min-h-[380px]'
        }
        style={{ backgroundColor: surface }}
      >
        {hasApiKey ? (
          <LocationMapWithGoogleApi
            apiKey={apiKey}
            latitude={latitude}
            longitude={longitude}
            name={name}
            mapPreviewLabel={mapPreviewLabel}
            isDark={isDark}
            tall={!compact}
            interactive={interactive}
            onPositionChange={onPositionChange}
          />
        ) : (
          <MapSurfacePlaceholder
            isDark={isDark}
            tall={!compact}
            label={mapUnavailableLabel}
          />
        )}
      </div>
      {hasApiKey && interactive && dragHintLabel ? (
        <p className="px-0.5 text-xs text-muted-foreground text-pretty">
          {dragHintLabel}
        </p>
      ) : null}
    </div>
  );

  if (!isValid) {
    if (bare) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 bg-muted/30 p-8">
          <MapPin className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{noCoordinatesLabel}</p>
        </div>
      );
    }
    return (
      <Card className="overflow-hidden">
        <CardContent className="flex flex-col items-center justify-center gap-2 bg-muted/30 p-8">
          <MapPin className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{noCoordinatesLabel}</p>
        </CardContent>
      </Card>
    );
  }

  if (bare) {
    return mapSurface;
  }

  return (
    <Card className="overflow-hidden border border-border bg-card">
      <div className="border-b border-border bg-muted/30 px-3 py-2">
        <p className="text-xs font-medium text-muted-foreground">
          {mapPreviewLabel}
          {name ? ` · ${name}` : ''}
        </p>
      </div>
      <CardContent className="p-0">{mapSurface}</CardContent>
    </Card>
  );
}
